import { useField } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useFeature, useLocalize } from '@treams/treams-hooks';
import { CheckmarkIcon, HelpIcon, VisibilityOffIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Callout,
	Checkbox,
	FlexGap,
	Heading,
	IconWrapper,
	Label,
	NumberedSubheading,
	Paragraph,
	QuillInput,
	QuillText,
	Radio,
	StarRating,
	StarRatingElement,
	Subheading,
	Tooltip,
} from '@treams/treams-ui';

import VoiceMemoTarget from '../../../../../../packages/@treams/treams-app/src/components/voice-memos/VoiceMemoTarget';
import { FeedbackQuestionMoreInformation } from '../../../../../feedback/FeedbackQuestionMoreInformation';
import { FeedbackSuggestionsAI } from '../../../../../feedback/results/FeedbackSuggestionsAI';
import GoalCard from '../../../../../goals/GoalCard';
import CompetencyLevelsInput from '../../../common/containers/CompetencyInput';
import FeedbackComposerGoalCard from '../composer/FeedbackComposerGoalCard';

const Answer = styled(Box)`
	border: 1px solid ${(props) => props.theme.tokens.border.subtle};
	border-radius: ${(props) => props.theme.tokens.radius.md}px;
	background: ${(props) => props.theme.tokens.surface.secondary};
`;

const StyledTooltip = styled(Tooltip)`
	&:hover {
		text-decoration: underline;
		cursor: pointer;
	}
`;

const HiddenFromSubjectNote = styled(Flex)`
	align-items: center;
	color: ${(props) => props.theme.tokens.text.secondary};
`;

const NotObserved = styled(Flex)`
	color: ${(props) => props.theme.tokens.info.text};

	> svg {
		width: 18px;
		height: 18px;
		color: ${(props) => props.theme.tokens.info.icon};
		path {
			fill: currentColor;
		}
	}
`;

export default function FeedbackResponseAnswer(props: {
	question: graphql.FeedbackRequestQuestion;
	answer?: graphql.FeedbackResponseAnswer;
	error?: boolean;
	name: string;
	index: number;
	viewerCanUpdate: boolean;
}) {
	const { localize } = useLocalize();
	const { t } = useTranslation();
	const hasAI = useFeature(graphql.FeatureName.Ai);
	const hasVoiceMemos = useFeature(graphql.FeatureName.FeedbackVoiceMemos);
	const hasCompetencyNotObservedOption = useFeature(
		graphql.FeatureName.CompetencyFeedbackNotObservedOption,
	);

	const [{ value: text }] = useField(`${props.name}.text`);
	const [{ value: voiceMemo }, , { setValue: setVoiceMemo }] = useField(`${props.name}.voiceMemo`);
	const [, { value: competency }, { setValue: setCompetency }] = useField(
		`${props.name}.competencyLevelIndex`,
	);
	const [{ value: selectedOptionID }, , { setValue: setSelectedOptionID }] = useField(
		`${props.name}.selectedOptionID`,
	);

	const isNotObserved = competency === null;
	const optionsSideBySide = props.question.options?.length === 2;

	return (
		<Box pt={2} pb={3}>
			<NumberedSubheading index={props.index + 1}>
				<Heading>
					{props.question.localizedText
						? localize(props.question.localizedText).text
						: props.question.text}
				</Heading>
				{props.question.subTitle && <Paragraph mt={2}>{props.question.subTitle}</Paragraph>}
				{!QuillText.isEmpty(props.question.additionalDetails) && (
					<FeedbackQuestionMoreInformation additionalDetails={props.question.additionalDetails} />
				)}
			</NumberedSubheading>
			<Flex mt={3}>
				<Box flexGrow={1} pl="42px">
					{props.question.competency && (
						<Box mb={3}>
							<FlexGap
								gap={2}
								mb={hasCompetencyNotObservedOption ? 2 : 0}
								justifyContent="space-between"
								alignItems="center"
							>
								{props.question.competency.description ? (
									<Tooltip
										content={
											<QuillText value={localize(props.question.competency.description).text} />
										}
									>
										<Flex mb={2} alignItems="center" justifyContent="center">
											<Subheading>{localize(props.question.competency.title).text}</Subheading>
							<IconWrapper ml={1} size={16}>
								<HelpIcon />
							</IconWrapper>
										</Flex>
									</Tooltip>
								) : (
									<Subheading mb={2}>{localize(props.question.competency.title).text}</Subheading>
								)}
								{hasCompetencyNotObservedOption && (
									<StyledTooltip
										content={
											<FlexGap gap={4} flexDirection="column" alignItems="center">
												{t('feedback.answer.not.observed.explanation')}
												<Checkbox
													checked={isNotObserved}
													label={t('feedback.answer.not.observed')}
													onClick={() =>
														!isNotObserved ? setCompetency(null) : setCompetency(undefined)
													}
												/>
											</FlexGap>
										}
									>
										{isNotObserved ? (
											<NotObserved>
												<CheckmarkIcon />
												{t('feedback.answer.not.observed')}
											</NotObserved>
										) : (
											t('feedback.answer.not.observed.question')
										)}
									</StyledTooltip>
								)}
							</FlexGap>
							{(competency !== null || !hasCompetencyNotObservedOption) && (
								<CompetencyLevelsInput
									party={graphql.EvaluationSubmissionParty.Manager}
									competency={props.question.competency}
									name={`${props.name}.competencyLevelIndex`}
									suffix=""
								/>
							)}
						</Box>
					)}
					{props.question.goal && (
						<Box mb={3}>
							<FeedbackComposerGoalCard goal={props.question.goal} />
						</Box>
					)}
					{props.question.goalRevision && (
						<Box mb={3}>
							<GoalCard goal={props.question.goalRevision} modal />
						</Box>
					)}
					{props.error && (
						<Callout color="red" mb={3}>
							{t('feedback.answer.required')}
						</Callout>
					)}
					{!(props.question.canSubjectView ?? true) && (
						<HiddenFromSubjectNote mb={2}>
							<IconWrapper mr={1} size={16}>
								<VisibilityOffIcon />
							</IconWrapper>
							<Paragraph>{t('feedback.answer.hiddenFromEmployee.note')}</Paragraph>
						</HiddenFromSubjectNote>
					)}
					{(props.question.type === graphql.FeedbackQuestionType.Stars ||
						props.question.type === graphql.FeedbackQuestionType.Both) && (
						<Box pb={3}>
							{props.viewerCanUpdate && (
								<Label mb={2}>
									{t('feedback.answer.howDidUserPerform')}
								</Label>
							)}
							{props.viewerCanUpdate ? (
								<StarRating name={`${props.name}.stars`} />
							) : (
								<StarRatingElement
									value={props.answer?.stars ?? 0}
									name={`${props.name}.stars`}
									disabled
								/>
							)}
						</Box>
					)}

					{props.question.type === graphql.FeedbackQuestionType.MultipleChoice && (
						<Box pb={3}>
							<FlexGap gap={8} flexDirection={optionsSideBySide ? 'row' : 'column'}>
								{props.question.options?.map((option) => {
									const selected =
										(props.viewerCanUpdate ? selectedOptionID : props.answer?.selectedOptionID) ===
										option.id;

									return (
										<Box
											key={option.id}
											flexGrow={0}
											flexShrink={0}
											style={{
												...(optionsSideBySide
													? {
															flexBasis: 'calc((100% - 16px) / 2)',
															maxWidth: 'calc((100% - 16px) / 2)',
														}
													: undefined),
												...(props.viewerCanUpdate ? undefined : { pointerEvents: 'none' }),
											}}
										>
											<Radio.Card
												checked={selected}
												title={localize(option.text).text}
												onChange={() => props.viewerCanUpdate && setSelectedOptionID(option.id)}
											/>
										</Box>
									);
								})}
							</FlexGap>
						</Box>
					)}

					{(props.question.type === graphql.FeedbackQuestionType.Text ||
						props.question.type === graphql.FeedbackQuestionType.Both) && (
						<Box pb={3}>
							{props.viewerCanUpdate &&
								!props.question.subTitle &&
								(props.question.canSubjectView ?? true) && (
									<Label mb={2}>
										{props.question.competency
											? t('feedback.answer.explainOptional')
											: t('feedback.answer.explain')}
									</Label>
								)}
							{props.viewerCanUpdate ? (
								<FeedbackTextInput
									hasVoiceMemos={hasVoiceMemos}
									voiceMemo={voiceMemo}
									setVoiceMemo={setVoiceMemo}
									hasAI={hasAI}
									question={props.question}
									name={props.name}
									text={text}
								/>
							) : (
								<Answer p={3}>
									<QuillText
										value={props.answer?.text}
										placeholder={t('feedback.answer.emptyTextQuestionPlaceholder')}
									/>
								</Answer>
							)}
						</Box>
					)}
				</Box>
			</Flex>
		</Box>
	);
}

function FeedbackTextInput(props: {
	hasVoiceMemos: boolean;
	voiceMemo: any;
	setVoiceMemo: (value: any) => void;
	hasAI: boolean;
	question: graphql.FeedbackRequestQuestion;
	name: string;
	text: any;
}) {
	const { localize } = useLocalize();
	const { t } = useTranslation();

	const textInput = props.hasAI ? (
		<FeedbackSuggestionsAI
			name="feedback.answer.suggestImprovements"
			context={{
				question: props.question.localizedText
					? localize(props.question.localizedText).text
					: props.question.text,
				answer: props.text?.ops.map((op: any) => op.insert ?? '').join('') ?? '',
			}}
			tooltip={t('feedback.answer.ai.button')}
			offsetTop={props.hasVoiceMemos ? 40 : 0}
		>
			<QuillInput.Formik name={`${props.name}.text`} />
		</FeedbackSuggestionsAI>
	) : (
		<QuillInput.Formik name={`${props.name}.text`} />
	);

	if (!props.hasVoiceMemos) {
		return textInput;
	}

	return (
		<VoiceMemoTarget voiceMemo={props.voiceMemo} onChange={props.setVoiceMemo}>
			{textInput}
		</VoiceMemoTarget>
	);
}
